import React, { useMemo } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { components, SingleValue } from 'react-select';
import {
  IRelationalOperator,
  OperandType,
  RelationalOperatorKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { ReactSelectMenu } from '../../../../Dropdown/StyledReactSelect';
import { BasicDropdownIndicator } from '../../../../Dropdown/StyledReactSelect/SelectUtils';
import { colors } from '../../../../../config/styles';
import {
  castToOptionalFieldFragment,
  IPredicateFormFieldPath,
} from '../../../types';
import useFilterWidgetFormContext from '../../../utils/useFilterWidgetFormContext';
import { useResetPredicates } from '../../../utils/useResetPredicates';
import { getPredicateFormFieldPath } from '../../../utils/formPath';

interface IProps {
  formPath: IPredicateFormFieldPath;
}

const RESTRICTED_FIELD_OPS: RelationalOperatorKey[] = [
  RelationalOperatorKey.Contains,
  RelationalOperatorKey.NotContains,
  RelationalOperatorKey.StartsWith,
  RelationalOperatorKey.NotStartsWith,
  RelationalOperatorKey.EndsWith,
  RelationalOperatorKey.NotEndsWith,
  RelationalOperatorKey.Matches,
  RelationalOperatorKey.NotMatches,
  RelationalOperatorKey.Exists,
];

const placeholder = 'Operator';

const PredicateOperator = ({ formPath }: IProps) => {
  const fieldPath = getPredicateFormFieldPath(formPath);

  const { control } = useFilterWidgetFormContext();

  const { field, fieldState } = useController({
    name: `${fieldPath}.relOp`,
    control,
  });

  const selectedOperandType = useWatch({
    name: `${fieldPath}.operand.type`,
    control,
  });

  const selectedField = useWatch({
    name: `${fieldPath}.field`,
    control,
  });

  const resetPredicates = useResetPredicates({
    formPath,
  });

  const options: IRelationalOperator[] = useMemo(() => {
    const values = castToOptionalFieldFragment(selectedField)?.relOps;

    if (!values?.length) {
      return [];
    }

    // for field operands list would be filtered
    if (selectedOperandType === OperandType.Field) {
      return values.filter(
        (relOp) => !RESTRICTED_FIELD_OPS.includes(relOp.key),
      );
    }

    return values;
  }, [selectedField, selectedOperandType]);

  const handleChange = (newValue: SingleValue<IRelationalOperator>) => {
    if (!newValue) {
      return;
    }

    field.onChange(newValue);

    resetPredicates(selectedOperandType);
  };

  const hasError = Boolean(fieldState.error);

  return (
    <ReactSelectMenu
      ref={field.ref}
      isMulti={false}
      options={options}
      onChange={handleChange}
      getOptionLabel={(relOp) => relOp.name}
      getOptionValue={(relOp) => relOp.key}
      value={field.value}
      components={{
        DropdownIndicator: (props) => BasicDropdownIndicator({ props }),
        SingleValue: components.SingleValue,
      }}
      styles={{
        control: (base) => {
          const result = {
            ...base,
            minWidth: '100%',
            maxWidth: '100%',
          };

          if (hasError) {
            result.borderColor = colors.mainError;
          }

          return result;
        },
        valueContainer: (base) => ({
          ...base,
          paddingLeft: '8px',
        }),
        placeholder: (base) => ({
          ...base,
          margin: 0,
        }),
      }}
      placeholder={placeholder}
      isDisabled={!selectedField}
      isSearchable={false}
      isClearable={false}
      openMenuOnFocus
      hideSelectedOptions
      menuPortalTarget={document.body}
    />
  );
};

export default PredicateOperator;
